import { ng } from "../ngds";

// this file is manually copied so it stays in sync with
// server/sharedTypes.ts
// client/src/sharedTypes.ts

export type Settings = IUserSettings;
// --- Material related interfaces ---

export import AppType = ng.frontend.general.SimplexApp;

export type WebResponse<TData> =
  | {
      kind: "data";
      data: TData;
      pagination: {
        CurrentPage: number;
        PageSize: number;
        TotalItemCount: number;
        TotalPageCount: number;
      };
      status: number;
    }
  | { kind: "error"; error: unknown; status: number };

export type WebResponseNoPagination<TData> =
  | {
      kind: "data";
      data: TData;
      status: number;
    }
  | { kind: "error"; error: unknown; status: number };

export type MaterialsFetchGuidResponse = WebResponseNoPagination<ng.material.Data_json[]>;

interface TMaterialInfo<TKind, TType, TData, TSubType> {
  kind: TKind;
  type: TType;
  data: TData;
  subType: TSubType;
}
export type MaterialInfo =
  | TMaterialInfo<"concrete", ng.material.concrete.Type, ng.material.concrete.CharacteristicData | null, "">
  | TMaterialInfo<"steel", ng.material.steel.Sort, ng.material.steel.CharacteristicData | null, "">
  | TMaterialInfo<"timber", "kSolid", ng.material.timber.solid.CharacteristicData, "solid_">
  | TMaterialInfo<"timber", "kBoard", ng.material.timber.board.CharacteristicData, "board_">
  | TMaterialInfo<"reinforcement", "kBar" | "kWire", ng.material.reinforcement.CharacteristicData | null, "">
  | TMaterialInfo<"fireSteel", ng.material.Mtrl, ng.fire_protection.steel.CharacteristicData | null, "">;
export type MaterialInfo_json =
  | TMaterialInfo<"concrete", ng.material.concrete.Type, ng.material.concrete.CharacteristicData_json | null, "">
  | TMaterialInfo<"steel", ng.material.steel.Sort, ng.material.steel.CharacteristicData_json | null, "">
  | TMaterialInfo<"timber", "kSolid", ng.material.timber.solid.CharacteristicData_json, "solid_">
  | TMaterialInfo<"timber", "kBoard", ng.material.timber.board.CharacteristicData_json, "board_">
  | TMaterialInfo<"reinforcement", "kBar" | "kWire", ng.material.reinforcement.CharacteristicData_json | null, "">
  | TMaterialInfo<"fireSteel", ng.material.Mtrl, ng.fire_protection.steel.CharacteristicData_json | null, "">;

export type ConversionValuesType = string | ng.material.concrete.Type | ng.material.steel.Sort | ng.material.Mtrl;

// type subType are now part of MaterialInfo, see ConversionValues type
// for vals use helper function extractValsFromMaterialInfo in mtrl/fetch
export interface IConversionValues {
  dbMtrl: ng.material.Data;
  id: ng.utils.ID | null;
  materialCategory: ng.material.Mtrl;
}
export interface IConversionValues_json {
  dbMtrl: ng.material.Data_json;
  id: ng.utils.ID_json | null;
  materialCategory: ng.material.Mtrl;
}
export type ConversionValues = IConversionValues & MaterialInfo;
export type ConversionValues_json = IConversionValues_json & MaterialInfo_json;
export type RestMaterial = WebResponseNoPagination<ConversionValues_json>;

// party based on the c# class from ng-asset\AssetService\Controllers\MaterialAssetController.cs
export interface MaterialListEntry {
  guid: string;
  id: string;
  name: string;
  type: string;
  ownertype: ng.utils.Owner;
  product: number;
  /** National annex the asset database files it under; read it as the country it is sold in. */
  annex?: ng.eurocode.Annex;
  dias: number[] | undefined; // only added by client
}

export type MaterialKey =
  | "Undefined"
  | "Steel"
  | "Concrete"
  | "Timber"
  | "Soil"
  | "Reinforcement"
  | "Fire protection steel";

// index needs to match
// ng-asset\AssetService\Controllers\MaterialAssetController.cs
export const MATERIAL_KEY_FROM_MATERIAL_INDEX: Array<MaterialKey> = [
  "Undefined",
  "Steel",
  "Concrete",
  "Timber",
  "Soil",
  "Reinforcement",
  "Fire protection steel",
];

/// A "list" of materials grouped on the key
export interface MaterialList {
  [key: string]: Array<MaterialListEntry> | undefined;
}

export type RestMaterialList = WebResponse<MaterialList>;

// --- User related interfaces ---

export interface IFile {
  id: number;
  guid: string;
  data: ng.frontend.column.Project_json;
  name: string;
  FileType: AppType;
}

export interface IUserQuantityItem {
  name: string;
  unit: string;
  size: number;
}

export interface IUserQuantitySettings {
  standard: string;
  length: IUserQuantityItem;
  force: IUserQuantityItem;
  moment: IUserQuantityItem;
  lineload: IUserQuantityItem;
  angle: IUserQuantityItem;
  stress: IUserQuantityItem;
  strain: IUserQuantityItem;
  mass: IUserQuantityItem;
  sectional: IUserQuantityItem;
  temperature: IUserQuantityItem;
  deformation: IUserQuantityItem;
  energy: IUserQuantityItem;
  power: IUserQuantityItem;
  utilisation: IUserQuantityItem;
}

export interface IUserColor {
  x: number;
  y: number;
  z: number;
  hex: string;
}

export interface IUserDefaultValues {
  company: string;
  material: ng.material.Mtrl;
  nationalAnnex: ng.eurocode.Annex;
  signature: string;
  generation: ng.eurocode.Generation;
}

// --- Print Visibility ---

export interface IPrintComponentVisibility {
  label: string;
  status: boolean;
}

export interface IPrintLayoutVisibility {
  label: string;
  status: boolean;
  components: IPrintComponentVisibility[];
}

export interface IPrintExtentVisibility {
  label: string;
  layouts: IPrintLayoutVisibility[];
}

export const DEFAULT_GRAPH_SCALE = 0.2;

export interface IUserSettings {
  tempProject: any;
  gridSquareSize: number;
  analyseOnSave: boolean;
  gridSquare: number;
  snapTogridSense: number;
  willSnapToGrid: boolean;
  filled: boolean;
  beamThickness: number;
  graphScaleFactor: number;
  graphNumberDecimal: number;
  graphNumberDecimalDeformations: number;
  invertZoomMouse: boolean;
  autoZoomViewportEnabled: boolean;
  defaultProject: number;
  lastSelectedLoadCombName: string;
  quantity: IUserQuantitySettings;
  gridLineThickness: number;
  implicitSupports: boolean;
  showSupportId: boolean;
  showLCS: boolean;
  showSection: boolean;
  showCross: boolean;
  pyshicalResp: boolean;
  proportionalLoads: boolean;
  showMaterial: boolean;
  showBeamId: boolean;
  showReactions: boolean;
  showGrid: boolean;
  hideUnusedLoads: boolean;
  lang: Lang;
  majorLineWidth: number;
  minorLineWidth: number;
  majorLineEach: number;
  darkMode: boolean;
  gridColorLight: IUserColor;
  lineColorLight: IUserColor;
  lineMajorColorLight: IUserColor;
  graphStaticColors: boolean;
  graphPositiveColorLight: IUserColor;
  graphNegativeColorLight: IUserColor;
  gridColorDark: IUserColor;
  lineColorDark: IUserColor;
  lineMajorColorDark: IUserColor;
  graphPositiveColorDark: IUserColor;
  graphNegativeColorDark: IUserColor;
  showDisclaimer: boolean;
  lastReleaseNoteShown: string;
  defaultValues: IUserDefaultValues;
  printVisibility: IPrintExtentVisibility[];
  graphScale: number;
  useHub: boolean; // deprecated
  showSurvicateSurvey: boolean;
  showSimplexCenterHint: boolean;
}

// --- Design reporter ---

export interface IReportedDesignChange {
  id: string;
  type: string;
  previous: string;
  suggested: string;
}

// --- Release notes ---

export interface IReleaseEntry {
  version: string;
  date: string;
  notes: ILangItem[];
}

export interface IReleaseNotes {
  releases: IReleaseEntry[];
}

// --- Localisation database ---

export interface IEntry {
  length?: IStandard | undefined;
  force?: IStandard | undefined;
  moment?: IStandard | undefined;
  lineload?: IStandard | undefined;
  angle?: IStandard | undefined;
  stress?: IStandard | undefined;
  strain?: IStandard | undefined;
  mass?: IStandard | undefined;
  sectional?: IStandard | undefined;
  temperature?: IStandard | undefined;
  deformation?: IStandard | undefined;
  energy?: IStandard | undefined;
  power?: IStandard | undefined;
  utilisation?: IStandard | undefined;
  label?: ILangItem | undefined;
  description?: ILangItem | undefined;
  quantity?: string[] | undefined;
  symbol?: IStandard | undefined;
  slug?: ILangItem | undefined;
}

interface IStandard {
  SI: string | INameSizeUnit;
  IMP: string | INameSizeUnit;
}
export type Std = "SI" | "IMP";

interface INameSizeUnit {
  name: string;
  unit: string;
  size: number | string;
}

export interface ILangItem {
  en: string;
  sv?: string | undefined;
  da?: string | undefined;
}

export type Lang = "en" | "sv" | "da" | "fi";

export interface IAPICheckResponse {
  access: boolean;
  status: number;
  statusText: string;
}

export interface ITranslationDatabase {
  [key: string]: IEntry;
}

export interface IStageDictionary {
  [index: string]: ng.stage.Support_json;
}

export interface ISectionForce {
  pos: number;
  N: {
    max: number;
    min: number;
  };
  V1: {
    max: number;
    min: number;
  };
  V2: {
    max: number;
    min: number;
  };
  T: {
    max: number;
    min: number;
  };
  M1: {
    max: number;
    min: number;
  };
  M2: {
    max: number;
    min: number;
  };
  el: string;
}

export interface ISectionForceDictionary {
  [index: string]: { [index: string]: ISectionForce[] };
}

export interface IReactionForce {
  pos: number;
  Rx: {
    max: number;
    min: number;
  };
  Ry: {
    max: number;
    min: number;
  };
  Rz: {
    max: number;
    min: number;
  };
  Mx: {
    max: number;
    min: number;
  };
  My: {
    max: number;
    min: number;
  };
  Mz: {
    max: number;
    min: number;
  };
}

export interface IReactionForceDictionary {
  [index: string]: { [index: string]: IReactionForce[] };
}

export interface IDisplacement {
  pos: number;
  u: {
    max: number;
    min: number;
  };
  v: {
    max: number;
    min: number;
  };
  w: {
    max: number;
    min: number;
  };
  x: {
    max: number;
    min: number;
  };
  y: {
    max: number;
    min: number;
  };
  z: {
    max: number;
    min: number;
  };
}

export interface IDisplacementDictionary {
  [index: string]: { [index: string]: IDisplacement[] };
}

export interface IDesignResult {
  check: string;
  utilisation: number;
  pos: number;
  lco: string;
  // The named quantities the control's own formula is written with - the same values that appear in
  // its MathML. Which keys are present depends on the control; a moment control reports "N", "M"
  // (moment from load), "Madd" (additional moment from second-order effects), "MEd" (M + Madd),
  // "MRd" and "util". Optional: an engine that reports no value map leaves it undefined.
  values?: { [key: string]: number };
}

export interface IDesignResultDictionary {
  [index: string]: { [index: string]: IDesignResult[] };
}
